import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { api, CATEGORIES, findCategory } from "@/lib/api";
import { LinkedCircles, DotGrid, CircleOutline, HairlineCross } from "@/components/Shapes";

// Leah's own photography — real, personal, on-brand
const HERO_IMG =
    "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/2pfrihrm_PXL_20250529_140137157.MP~2.webp";
const SIDE_IMG =
    "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/5sb4s0dc_IMG_2638.webp";

export default function Home() {
    const [essays, setEssays] = useState([]);
    const location = useLocation();

    useEffect(() => {
        api.get("/essays", { params: { limit: 4 } })
            .then((r) => setEssays(r.data))
            .catch(() => setEssays([]));
    }, []);

    // Scroll to hash on mount / hash change (e.g., arriving from /#collections)
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace("#", "");
            // Wait a tick so the section is mounted
            const timer = setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 250);
            return () => clearTimeout(timer);
        }
    }, [location.hash]);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="pt-32 md:pt-40 pb-14 md:pb-20 relative overflow-hidden">
                <div className="mx-auto max-w-[1280px] px-6 md:px-10">
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                        <div className="lg:col-span-6 relative">
                            <div className="flex items-center gap-4">
                                <div className="h-px w-10 bg-[rgb(var(--orange))]" />
                                <p className="overline" data-testid="hero-overline">
                                    Field Notes from Leah Moschella
                                </p>
                            </div>

                            <h1 className="mt-6 text-3xl md:text-[42px] lg:text-5xl font-bold text-[rgb(var(--teal))] tracking-tight leading-[1.08]">
                                Overachieving For Me:
                                <br />
                                <span className="italic font-normal text-[rgb(var(--orange))]">
                                    Notes from A Recovering People Pleasing Perfectionist
                                </span>
                            </h1>

                            <p className="mt-6 text-[17px] md:text-lg text-[rgb(var(--ink-2))] leading-relaxed max-w-xl">
                                Making sense of the world with equal parts skepticism, humor, and hope.
                            </p>

                            <div className="mt-8 flex flex-wrap gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const el = document.getElementById("collections");
                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}
                                    className="btn-primary"
                                    data-testid="hero-cta-essays"
                                >
                                    Read the Stories →
                                </button>
                                <Link to="/about" className="btn-ghost" data-testid="hero-cta-about">
                                    About Leah
                                </Link>
                            </div>
                        </div>

                        {/* Hero image column */}
                        <div className="lg:col-span-6 relative">
                            <div className="absolute -top-6 -right-6 hidden md:block z-0">
                                <DotGrid rows={5} cols={5} gap={14} size={3} color="teal" />
                            </div>
                            <motion.div
                                initial={{ opacity: 0, scale: 1.04 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                className="frame aspect-[4/5] w-full relative z-10"
                            >
                                <img
                                    src={HERO_IMG}
                                    alt="Leah, on the water at sunset — a rare quiet moment between adventures."
                                />
                            </motion.div>
                            <div className="absolute -bottom-6 -left-4 md:-left-8 hidden md:block z-20">
                                <div className="bg-[rgb(var(--paper))] border hairline px-5 py-4 max-w-[240px]">
                                    <p className="overline">Currently</p>
                                    <p className="mt-2 text-sm text-[rgb(var(--ink-2))] leading-relaxed italic">
                                        Learning that &ldquo;busy&rdquo; is not a personality.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="mx-auto max-w-[1280px] px-6 md:px-10">
                <div className="rule" />
            </div>

            {/* Intro copy — verbatim from user */}
            <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-16 md:py-24">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3">
                            <HairlineCross size={18} color="gold" />
                            <p className="overline">A Short Introduction</p>
                        </div>
                        <div className="mt-3 rule-orange" />
                    </div>
                    <div className="md:col-span-8 space-y-5 text-[17px] leading-relaxed text-[rgb(var(--ink-2))] max-w-2xl">
                        <p>
                            This site is a collection of stories about the mistakes worth making, the
                            questions worth asking, and the beautiful chaos that happens when curiosity
                            wins over perfection.
                        </p>
                        <p>
                            I used to think success meant collecting accomplishments. Then I realized, I
                            was waiting for someone to define my accomplishments.
                        </p>
                        <p>
                            These days I&apos;m collecting more stories than trophies. I&apos;m seeking
                            authentic community over transactional networking. And I&apos;m writing about
                            my experiences. Some of these stories center the conference room and a keynote.
                            Other stories dive deep into what happens when you tell a woman she&apos;s
                            truly a badass. Still others reconcile the complexity of what happens when you
                            define success for yourself and when hustle culture is for your own happiness.
                        </p>
                    </div>
                </div>
            </section>

            {/* Image band with a shape moment */}
            <section className="bg-[rgb(var(--paper-2))] py-16 md:py-20 relative overflow-hidden">
                <div className="mx-auto max-w-[1280px] px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5 relative">
                        <div className="absolute -top-3 -left-3 hidden md:block">
                            <CircleOutline size={70} color="orange" stroke={1.25} />
                        </div>
                        <div className="frame aspect-[5/4] relative z-10">
                            <img
                                src={SIDE_IMG}
                                alt="Leah in Antarctica — arms open on the rocks, mountains and icebergs behind her."
                            />
                        </div>
                    </div>
                    <div className="md:col-span-7 md:pl-6">
                        <p className="overline">The Idea</p>
                        <h2 className="mt-3 h-section max-w-lg">
                            Stories, not trophies. Community, not connections.
                        </h2>
                        <p className="mt-5 text-[17px] leading-relaxed text-[rgb(var(--ink-2))] max-w-xl">
                            Three collections of essays and musing. Choose your own adventure.
                        </p>
                        <Link to="/about" className="mt-6 inline-flex btn-text" data-testid="home-idea-cta">
                            Meet Leah →
                        </Link>
                    </div>
                </div>
            </section>

            {/* The three collections */}
            <section id="collections" className="mx-auto max-w-[1280px] px-6 md:px-10 py-16 md:py-24 scroll-mt-24">
                <div className="flex items-end justify-between gap-4 mb-10">
                    <div>
                        <p className="overline">The Collections</p>
                        <h2 className="mt-2 h-section">Three places to wander.</h2>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                    {CATEGORIES.map((c, i) => (
                        <motion.div
                            key={c.key}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                        >
                            <Link
                                to={`/${c.key}`}
                                className="group block h-full card-editorial relative"
                                data-testid={`home-section-${c.key}`}
                            >
                                <div className="absolute top-4 right-4 opacity-70 group-hover:opacity-100 transition-opacity">
                                    <LinkedCircles size={44} color={i === 1 ? "orange" : i === 2 ? "teal" : "gold"} stroke={1.5} />
                                </div>
                                <p className="overline text-[rgb(var(--gold))]">0{i + 1}</p>
                                <h3 className="mt-4 text-xl md:text-2xl font-bold text-[rgb(var(--teal))] group-hover:text-[rgb(var(--orange))] transition-colors leading-tight">
                                    {c.label}
                                </h3>
                                <p className="mt-3 text-[15px] text-[rgb(var(--ink-2))] leading-relaxed">
                                    {c.blurb}
                                </p>
                                <span className="mt-6 inline-block btn-text">Enter →</span>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Field Notes — a strip of Leah's real, unfiltered life */}
            <div className="mx-auto max-w-[1280px] px-6 md:px-10">
                <div className="rule" />
            </div>
            <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-16 md:py-24">
                <div className="flex items-end justify-between gap-4 mb-10">
                    <div>
                        <p className="overline">Field Notes</p>
                        <h2 className="mt-2 h-section">Currently, and recently.</h2>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {[
                        {
                            src: "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/b12uclcm_lnm6.jpg",
                            alt: "Leah in an ornate marble hall.",
                            caption: "Wandered into a jewel box. Refused to leave.",
                        },
                        {
                            src: "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/zn46nv7g_PXL_20251008_225241439~2.webp",
                            alt: "Leah on a plane, holding her passport.",
                            caption: "New stamp. New season.",
                        },
                        {
                            src: "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/9qi02wsi_PXL_20251226_131424364.webp",
                            alt: "Leah on a snowy mountain ridge.",
                            caption: "Further out than yesterday.",
                        },
                        {
                            src: "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/1gfk0xma_PXL_20250921_115608937.webp",
                            alt: "Leah at Oktoberfest with a stein.",
                            caption: "Oktoberfest. One liter in. Do not send help.",
                        },
                    ].map((it, i) => (
                        <motion.figure
                            key={i}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.06 }}
                            className="group"
                        >
                            <div className="frame aspect-[3/4]">
                                <img src={it.src} alt={it.alt} loading="lazy" />
                            </div>
                            <figcaption className="mt-3 text-[13px] italic text-[rgb(var(--ink-2))] leading-snug">
                                {it.caption}
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </section>

            {/* Latest essays */}
            {essays.length > 0 && (
                <>
                    <div className="mx-auto max-w-[1280px] px-6 md:px-10">
                        <div className="rule" />
                    </div>
                    <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-16 md:py-24">
                        <div className="flex items-end justify-between gap-4 mb-8">
                            <div>
                                <p className="overline">Recent</p>
                                <h2 className="mt-2 h-section">Latest essays.</h2>
                            </div>
                        </div>
                        <ul className="border-t hairline">
                            {essays.slice(0, 4).map((e) => {
                                const cat = findCategory(e.category);
                                return (
                                    <li key={e.id}>
                                        <Link
                                            to={`/essay/${e.slug}`}
                                            className="group grid md:grid-cols-12 gap-4 py-6 border-b hairline"
                                            data-testid={`home-essay-${e.slug}`}
                                        >
                                            <div className="md:col-span-3">
                                                <span className="overline">{cat?.short}</span>
                                            </div>
                                            <div className="md:col-span-7">
                                                <h3 className="text-lg md:text-xl font-bold text-[rgb(var(--teal))] group-hover:text-[rgb(var(--orange))] transition-colors">
                                                    {e.title}
                                                </h3>
                                                {e.subtitle && (
                                                    <p className="mt-1 text-[14px] italic text-[rgb(var(--ink-2))]">{e.subtitle}</p>
                                                )}
                                            </div>
                                            <div className="md:col-span-2 md:text-right text-xs text-[rgb(var(--ink-2))]/70">
                                                {e.read_minutes} min
                                            </div>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                </>
            )}
        </div>
    );
}
